import { useTranslation } from 'react-i18next'
import { Device } from '../types'
import { AssetStatus } from '../types/Asset'
import StatusIndicator from './StatusIndicator'
import { Tooltip } from '@mui/material'
import { Circle } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import { getDevice } from '../services/deviceService'
import { purple } from '@mui/material/colors'

/**
 * The props of the device status indicator.
 */
export interface DeviceStatusIndicatorProps {
  /**
   * The device.
   */
  device: Device

  /**
   * The font size.
   */
  fontSize?: 'inherit' | 'small' | 'medium' | 'large'

  /**
   * Indicates if the status should updated automatically.
   */
  autoUpdate?: boolean
}

/**
 * An indicator for the device status.
 */
export default function DeviceStatusIndicator({
  device,
  fontSize = 'inherit',
  autoUpdate = false
}: DeviceStatusIndicatorProps): JSX.Element {
  /**
   * The translation function.
   */
  const { t } = useTranslation('common')

  /**
   * The device status.
   */
  const [status, setStatus] = useState<boolean>(device.status)

  /**
   * Update the device status.
   */
  const updateStatus = async (signal: AbortSignal) => {
    try {
      const { status } = await getDevice(device.name, {
        signal
      })
      setStatus(status)
    } catch (_error) {
      // Ignore the error.
    }
  }

  /**
   * Update the status if the device changes.
   */
  useEffect(() => {
    setStatus(device.status)
  }, [device])

  /**
   * Load the status if auto update is enabled for every minute.
   */
  useEffect(() => {
    if (autoUpdate) {
      const controller = new AbortController()

      const ONE_MINUTE = 60 * 1000
      const interval = setInterval(() => {
        updateStatus(controller.signal)
      }, ONE_MINUTE)

      return () => {
        controller.abort()
        clearInterval(interval)
      }
    }
  }, [device, autoUpdate])

  return (
    <>
      {device.isVirtual ? (
        <Tooltip title={t('virtualDevice')}>
          <Circle sx={{ color: purple[500] }} fontSize={fontSize} />
        </Tooltip>
      ) : device?.asset?.status === AssetStatus.INACTIVE ? (
        <Tooltip title={t('detached')}>
          <Circle color="disabled" fontSize={fontSize} />
        </Tooltip>
      ) : (
        <StatusIndicator status={status} fontSize={fontSize} />
      )}
    </>
  )
}
